import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { Type } from '../../../interfaces/type.interface';
import { Category } from '../../../interfaces/category.interface';
import { GetProductCategoriesResponse } from '../interfaces/get-product-categories';
import { GetProductTypesResponse } from '../interfaces/get-product-types';
import { GetFormatsResponse } from '../interfaces/get-formats';
import { GetGenresResponse } from '../interfaces/get-genres';
import { Genre } from '../../../interfaces/genre.interface';
import { Format } from '../../../interfaces/format.interface';
import { PriceRange } from '../../../interfaces/priceRange.interface';
import { GetPriceRangesResponse } from '../interfaces/get-price-ranges';
import { GetWarehouseTypesResponse } from '../interfaces/get-warehouse-types';
import { GetLocationTypesResponse } from '../interfaces/get-location-types';
import { GetContainerTypesResponse } from '../interfaces/get-container-types';
import { GetChannelTypesResponse } from '../interfaces/get-channel-types';
import { GetContainerCategoriesResponse } from '../interfaces/get-container-categories';
import { GetReceptionTypesResponse } from '../interfaces/get-reception-types';
import { GetAcquisitionTypesResponse } from '../interfaces/get-adquisition-types';
import { ProductAttribute } from '../../../interfaces/productAttribute.interface';
import { GetProductUsagesResponse } from '../interfaces/get-product-usages';
import { GetProductPackagingsResponse } from '../interfaces/get-product-packagings';
import { GetProductFunctionalitiesResponse } from '../interfaces/get-product-functionalities';
import { GetProductAestheticsResponse } from '../interfaces/get-product-aesthetics';
import { GetProductStatesResponse } from '../interfaces/get-product-states';
import { GetProductCompletenessesResponse } from '../interfaces/get-product-completenesses';
import { GetContainerRecipientTypesResponse } from '../interfaces/get-container-recipient-types';
import { ContainerRecipientType } from '../../../interfaces/containerRecipient.interface';

// Adapter must be declared before initialState
const containerRecipientTypesAdapter = createEntityAdapter<ContainerRecipientType>((type) => type.id.toString());

export interface MasterState {
  productTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  warehouseTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  locationTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  containerTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  channelTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  acquisitionTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  receptionTypes: {
    ids: string[];
    entities: Record<string, Type>;
  };
  containerRecipientTypes: {
    ids: string[];
    entities: Record<string, ContainerRecipientType>;
  };
  type: Type | null;
  productCategories: {
    ids: string[];
    entities: Record<string, Category>;
  };
  containerCategories: {
    ids: string[];
    entities: Record<string, Category>;
  };
  category: Category | null;
  productUsages: {
    ids: string[];
    entities: Record<string, ProductAttribute>;
  };
  productPackagings: {
    ids: string[];
    entities: Record<string, ProductAttribute>;
  };
  productFuntionalities: {
    ids: string[];
    entities: Record<string, ProductAttribute>;
  };
  productAesthetics: {
    ids: string[];
    entities: Record<string, ProductAttribute>;
  };
  productCompletenesses: {
    ids: string[];
    entities: Record<string, ProductAttribute>;
  };
  productStates: {
    ids: string[];
    entities: Record<string, ProductAttribute>;
  };
  productAttribute: ProductAttribute | null;
  genres: {
    ids: string[];
    entities: Record<string, Genre>;
  };
  genre: Genre | null;
  formats: {
    ids: string[];
    entities: Record<string, Format>;
  };
  format: Format | null;
  priceRanges: {
    ids: string[];
    entities: Record<string, PriceRange>;
  };
  priceRange: PriceRange | null;
  errorCode: number | null;
}

const initialState: MasterState = {
  productTypes: {
    ids: [],
    entities: {},
  },
  warehouseTypes: {
    ids: [],
    entities: {},
  },
  locationTypes: {
    ids: [],
    entities: {},
  },
  containerTypes: {
    ids: [],
    entities: {},
  },
  channelTypes: {
    ids: [],
    entities: {},
  },
  acquisitionTypes: {
    ids: [],
    entities: {},
  },
  receptionTypes: {
    ids: [],
    entities: {},
  },
  containerRecipientTypes: {
    ids: [],
    entities: {},
  },
  type: null,
  productUsages: {
    ids: [],
    entities: {},
  },
  productPackagings: {
    ids: [],
    entities: {},
  },
  productFuntionalities: {
    ids: [],
    entities: {},
  },
  productAesthetics: {
    ids: [],
    entities: {},
  },
  productCompletenesses: {
    ids: [],
    entities: {},
  },
  productStates: {
    ids: [],
    entities: {},
  },
  productAttribute: null,
  productCategories: {
    ids: [],
    entities: {},
  },
  containerCategories: {
    ids: [],
    entities: {},
  },
  category: null,
  genres: {
    ids: [],
    entities: {},
  },
  genre: null,
  formats: {
    ids: [],
    entities: {},
  },
  format: null,
  priceRanges: {
    ids: [],
    entities: {},
  },
  priceRange: null,
  errorCode: null,
};

const attributesAdapter = createEntityAdapter<ProductAttribute>((attribute: ProductAttribute) => attribute.id);
const typesAdapter = createEntityAdapter<Type>((type: Type) => type.id);
const categoriesAdapter = createEntityAdapter<Category>((type: Category) => type.id);
const formatsAdapter = createEntityAdapter<Format>((format: Format) => format.id);
const genresAdapter = createEntityAdapter<Genre>((genre: Genre) => genre.id);
const priceRangesAdapter = createEntityAdapter<PriceRange>((priceRange: PriceRange) => priceRange.id);

const masterSlice = createSlice({
  name: 'master',
  initialState,
  reducers: {
    getTypesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getProductTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetProductTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.product_types, state.productTypes);
    },
    getWarehouseTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetWarehouseTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.warehouse_types, state.warehouseTypes);
    },
    getLocationTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetLocationTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.location_types, state.locationTypes);
    },
    getContainerTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetContainerTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.container_types, state.containerTypes);
    },
    getChannelTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetChannelTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.channel_types, state.channelTypes);
    },
    getAcquisitionTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetAcquisitionTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.acquisition_types, state.acquisitionTypes);
    },
    getReceptionTypesSuccessApiAction: (
      state,
      action: PayloadAction<GetReceptionTypesResponse>
    ) => {
      typesAdapter.addMany(action.payload.reception_types, state.receptionTypes);
    },
    getContainerRecipientTypesSuccessApiAction: (
      state,
      action: PayloadAction<{ containerRecipientTypes: ContainerRecipientType[] }>
    ) => {
      // Reemplazar completamente el listado para evitar residuos tras crear/eliminar
      const items = action.payload.containerRecipientTypes || [];
      state.containerRecipientTypes.ids = items.map((i) => i.id.toString());
      state.containerRecipientTypes.entities = items.reduce((acc, i) => {
        acc[i.id.toString()] = i;
        return acc;
      }, {} as Record<string, ContainerRecipientType>);
    },
    getTypesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getAttributesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getProductUsagesSuccessApiAction: (
      state,
      action: PayloadAction<GetProductUsagesResponse>
    ) => {
      attributesAdapter.addMany(action.payload.product_usage, state.productUsages);
    },
    getProductPackagingsSuccessApiAction: (
      state,
      action: PayloadAction<GetProductPackagingsResponse>
    ) => {
      attributesAdapter.addMany(action.payload.product_packaging, state.productPackagings);
    },
    getProductFuntionalitiesSuccessApiAction: (
      state,
      action: PayloadAction<GetProductFunctionalitiesResponse>
    ) => {
      attributesAdapter.addMany(action.payload.product_functionalities, state.productFuntionalities);
    },
    getProductAestheticsSuccessApiAction: (
      state,
      action: PayloadAction<GetProductAestheticsResponse>
    ) => {
      attributesAdapter.addMany(action.payload.product_aesthetic, state.productAesthetics);
    },
    getProductCompletenessesSuccessApiAction: (
      state,
      action: PayloadAction<GetProductCompletenessesResponse>
    ) => {
      attributesAdapter.addMany(action.payload.product_completeness, state.productCompletenesses);
    },
    getProductStatesSuccessApiAction: (
      state,
      action: PayloadAction<GetProductStatesResponse>
    ) => {
      attributesAdapter.addMany(action.payload.product_state, state.productStates);
    },
    getAttributesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getCategoriesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getProductCategoriesSuccessApiAction: (
      state,
      action: PayloadAction<GetProductCategoriesResponse>
    ) => {
      categoriesAdapter.addMany(action.payload.product_categories, state.productCategories);
    },
    getContainerCategoriesSuccessApiAction: (
      state,
      action: PayloadAction<GetContainerCategoriesResponse>
    ) => {
      categoriesAdapter.addMany(action.payload.container_categories, state.containerCategories);
    },
    getCategoriesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getFormatsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getFormatsSuccessApiAction: (
      state,
      action: PayloadAction<GetFormatsResponse>
    ) => {
      formatsAdapter.addMany(action.payload.product_formats, state.formats);
    },
    getFormatsFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getGenresInitApiAction: (state) => {
      state.errorCode = null;
    },
    getGenresSuccessApiAction: (
      state,
      action: PayloadAction<GetGenresResponse>
    ) => {
      genresAdapter.addMany(action.payload.product_genres, state.genres);
    },
    getGenresFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getPriceRangesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getPriceRangesSuccessApiAction: (
      state,
      action: PayloadAction<GetPriceRangesResponse>
    ) => {
      priceRangesAdapter.addMany(action.payload.price_ranges, state.priceRanges);
    },
    getPriceRangesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postTypeInitApiAction: (state) => {
      state.errorCode = null;
    },
    postTypeSuccessApiAction: () => {},
    postTypeFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postProductAttributeInitApiAction: (state) => {
      state.errorCode = null;
    },
    postProductAttributeSuccessApiAction: () => {},
    postProductAttributeFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postCategoryInitApiAction: (state) => {
      state.errorCode = null;
    },
    postCategorySuccessApiAction: () => {},
    postCategoryFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postFormatInitApiAction: (state) => {
      state.errorCode = null;
    },
    postFormatSuccessApiAction: () => {},
    postFormatFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postGenreInitApiAction: (state) => {
      state.errorCode = null;
    },
    postGenreSuccessApiAction: () => {},
    postGenreFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postPriceRangeInitApiAction: (state) => {
      state.errorCode = null;
    },
    postPriceRangeSuccessApiAction: () => {},
    postPriceRangeFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putTypeInitApiAction: (state) => {
      state.errorCode = null;
    },
    putTypeSuccessApiAction: () => {},
    putTypeFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putCategoryInitApiAction: (state) => {
      state.errorCode = null;
    },
    putCategorySuccessApiAction: () => {},
    putCategoryFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putFormatInitApiAction: (state) => {
      state.errorCode = null;
    },
    putFormatSuccessApiAction: () => {},
    putFormatFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putGenreInitApiAction: (state) => {
      state.errorCode = null;
    },
    putGenreSuccessApiAction: () => {},
    putGenreFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putPriceRangeInitApiAction: (state) => {
      state.errorCode = null;
    },
    putPriceRangeSuccessApiAction: () => {},
    putPriceRangeFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteTypeInitApiAction: () => {},
    deleteTypeSuccessApiAction: () => {},
    deleteTypeFailuireApiAction: () => {},
    putProductAttributeInitApiAction: (state) => {
      state.errorCode = null;
    },
    putProductAttributeSuccessApiAction: () => {},
    putProductAttributeFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    updateAttributesOrderAction: (
      state,
      action: PayloadAction<{ 
        type: 'usage' | 'packaging' | 'functionality' | 'aesthetic' | 'completeness' | 'state';
        items: ProductAttribute[];
      }>
    ) => {
      const { type, items } = action.payload;
      let targetState: { ids: string[]; entities: Record<string, ProductAttribute> };
      
      switch (type) {
        case 'usage':
          targetState = state.productUsages;
          break;
        case 'packaging':
          targetState = state.productPackagings;
          break;
        case 'functionality':
          targetState = state.productFuntionalities;
          break;
        case 'aesthetic':
          targetState = state.productAesthetics;
          break;
        case 'completeness':
          targetState = state.productCompletenesses;
          break;
        case 'state':
          targetState = state.productStates;
          break;
        default:
          return;
      }
      
      // Actualizar IDs en el orden nuevo
      targetState.ids = items.map(item => item.id.toString());
      
      // Actualizar entities con los nuevos cardinality
      items.forEach(item => {
        const id = item.id.toString();
        if (targetState.entities[id]) {
          targetState.entities[id] = {
            ...targetState.entities[id],
            cardinality: item.cardinality,
          };
        }
      });
    },
    deleteProductAttributeInitApiAction: () => {},
    deleteProductAttributeSuccessApiAction: () => {},
    deleteProductAttributeFailuireApiAction: () => {},
    deleteCategoryInitApiAction: () => {},
    deleteCategorySuccessApiAction: () => {},
    deleteCategoryFailuireApiAction: () => {},
    deleteFormatInitApiAction: () => {},
    deleteFormatSuccessApiAction: () => {},
    deleteFormatFailuireApiAction: () => {},
    deleteGenreInitApiAction: () => {},
    deleteGenreSuccessApiAction: () => {},
    deleteGenreFailuireApiAction: () => {},
    deletePriceRangeInitApiAction: () => {},
    deletePriceRangeSuccessApiAction: () => {},
    deletePriceRangeFailuireApiAction: () => {},
    setCategoryAction: (state, action: PayloadAction<Category | null>) => {
      state.category = action.payload;
    },
    setTypeAction: (state, action: PayloadAction<Type | null>) => {
      state.type = action.payload;
    },
    setProductAttributeAction: (state, action: PayloadAction<ProductAttribute | null>) => {
      state.productAttribute = action.payload;
    },
    setFormatAction: (state, action: PayloadAction<Format | null>) => {
      state.format = action.payload;
    },
    setGenreAction: (state, action: PayloadAction<Genre | null>) => {
      state.genre = action.payload;
    },
    setPriceRangeAction: (state, action: PayloadAction<PriceRange | null>) => {
      state.priceRange = action.payload;
    },
  },
});

export const {
  getTypesInitApiAction,
  getProductTypesSuccessApiAction,
  getWarehouseTypesSuccessApiAction,
  getLocationTypesSuccessApiAction,
  getContainerTypesSuccessApiAction,
  getChannelTypesSuccessApiAction,
  getAcquisitionTypesSuccessApiAction,
  getReceptionTypesSuccessApiAction,
  getContainerRecipientTypesSuccessApiAction,
  getTypesFailureApiAction,
  getCategoriesInitApiAction,
  getProductCategoriesSuccessApiAction,
  getContainerCategoriesSuccessApiAction,
  getCategoriesFailureApiAction,
  getFormatsInitApiAction,
  getFormatsSuccessApiAction,
  getFormatsFailureApiAction,
  getGenresInitApiAction,
  getGenresSuccessApiAction,
  getGenresFailureApiAction,
  getPriceRangesInitApiAction,
  getPriceRangesSuccessApiAction,
  getPriceRangesFailureApiAction,
  postTypeInitApiAction,
  postTypeSuccessApiAction,
  postTypeFailuireApiAction,
  postCategoryInitApiAction,
  postCategorySuccessApiAction,
  postCategoryFailuireApiAction,
  postFormatInitApiAction,
  postFormatSuccessApiAction,
  postFormatFailuireApiAction,
  postGenreInitApiAction,
  postGenreSuccessApiAction,
  postGenreFailuireApiAction,
  postPriceRangeInitApiAction,
  postPriceRangeSuccessApiAction,
  postPriceRangeFailuireApiAction,
  putTypeInitApiAction,
  putTypeSuccessApiAction,
  putTypeFailuireApiAction,
  putCategoryInitApiAction,
  putCategorySuccessApiAction,
  putCategoryFailuireApiAction,
  putFormatInitApiAction,
  putFormatSuccessApiAction,
  putFormatFailuireApiAction,
  putGenreInitApiAction,
  putGenreSuccessApiAction,
  putGenreFailuireApiAction,
  putPriceRangeInitApiAction,
  putPriceRangeSuccessApiAction,
  putPriceRangeFailuireApiAction,
  deleteTypeInitApiAction,
  deleteTypeSuccessApiAction,
  deleteTypeFailuireApiAction,
  deleteCategoryInitApiAction,
  deleteCategorySuccessApiAction,
  deleteCategoryFailuireApiAction,
  deleteFormatInitApiAction,
  deleteFormatSuccessApiAction,
  deleteFormatFailuireApiAction,
  deleteGenreInitApiAction,
  deleteGenreSuccessApiAction,
  deleteGenreFailuireApiAction,
  deletePriceRangeInitApiAction,
  deletePriceRangeSuccessApiAction,
  deletePriceRangeFailuireApiAction,
  getAttributesInitApiAction,
  getProductAestheticsSuccessApiAction,
  getProductCompletenessesSuccessApiAction,
  getProductFuntionalitiesSuccessApiAction,
  getProductPackagingsSuccessApiAction,
  getProductStatesSuccessApiAction,
  getProductUsagesSuccessApiAction,
  getAttributesFailureApiAction,
  postProductAttributeInitApiAction,
  postProductAttributeSuccessApiAction,
  postProductAttributeFailuireApiAction,
  putProductAttributeInitApiAction,
  putProductAttributeSuccessApiAction,
  putProductAttributeFailuireApiAction,
  updateAttributesOrderAction,
  deleteProductAttributeInitApiAction,
  deleteProductAttributeSuccessApiAction,
  deleteProductAttributeFailuireApiAction,
  setCategoryAction,
  setTypeAction,
  setProductAttributeAction,
  setFormatAction,
  setGenreAction,
  setPriceRangeAction,
} = masterSlice.actions;

export default masterSlice.reducer;
